import { useEffect } from "react";
import type { RefObject } from "react";

/**
 * KEEP THE FOCUSED INPUT ABOVE THE SOFT KEYBOARD.
 *
 * Reported on the mobile CRM card: *"on click I noticed the view does not focus on it, and
 * the keyboard is over it."* The quick-notes trigger sits on a row that is usually low on
 * the screen, the popover opens below it, its input auto-focuses, the keyboard slides up —
 * and nothing moves, so the realtor is typing into a box they cannot see.
 *
 * ── Why `visualViewport` and nothing else ───────────────────────────────────────
 *
 * The keyboard does not change the LAYOUT viewport on iOS Safari: `window.innerHeight`,
 * `100vh` and `getBoundingClientRect()` all keep reporting the full unshrunk screen, so
 * every measurement made from them says the input is comfortably visible while the keyboard
 * covers it. `window.visualViewport` is the only API that reports the region actually left
 * on screen, on both iOS Safari and Android Chrome.
 *
 * It is feature-detected because it does not exist in older browsers OR in jsdom — where
 * this must be a clean no-op rather than a throw, since both quick-notes surfaces render in
 * component tests that never open a keyboard.
 *
 * ── Why this scrolls the nearest SCROLLER, not `scrollIntoView` on the element ──
 *
 * Two reasons, and the first is fatal on its own:
 *
 *  1. **`scrollIntoView` aligns against the LAYOUT viewport**, which is exactly the
 *     coordinate space the keyboard does not shrink. It would happily "reveal" the input
 *     into the strip the keyboard is sitting on and consider the job done.
 *  2. The popover's Paper is **portalled to `document.body`** and positioned by popper.js
 *     against its anchor. Scrolling that node — rather than the document — moves the
 *     surface without moving the anchor it is pinned to, so the popover walks away from the
 *     button that opened it. Scrolling the SCROLLER moves anchor and popper together,
 *     because both keep their coordinates within it.
 *
 * ⚠️ AND THE SCROLLER IS NOT ALWAYS THE WINDOW. The dashboard shell is `overflow: clip`,
 * which deliberately clips WITHOUT becoming a scroll container, so on most pages the window
 * really is the scroller. But two of this hook's mount points sit inside panes that scroll
 * themselves (`overflowY: auto`), and there `window.scrollBy` is a silent no-op — it looks
 * fixed and does nothing. `nearestScroller` walks up and finds whichever it is.
 *
 * ── What it reveals: the FOCUSED descendant, not the container ───────────────────
 *
 * The caller hands in the SURFACE and the hook measures whatever inside it holds focus. The
 * obvious alternative — track the one input — helps only the first field: on a quick-notes
 * popover the field most likely to be under the keyboard is an EXISTING note being edited,
 * which is furthest down the list. Measuring the container instead is equally wrong in the
 * other direction: revealing 400px of notes pushes the box being typed in off the TOP.
 *
 * ── Why desktop needs no breakpoint check ───────────────────────────────────────
 *
 * With no keyboard the visual viewport IS the layout viewport, so `offsetTop + height`
 * equals the window height and a focused input that is already on screen can never be
 * "below the visible bottom". Nothing runs, nothing scrolls, and there is no `isMobile` to
 * keep in sync with the rest of the app.
 *
 * ⚠️ That is only true because the CLEARANCE IS NOT PART OF THE TEST. Adding it before the
 * comparison — the first version did — makes the condition true for any input within 12px
 * of the window bottom, so a desktop popover placed low scrolled the page by up to 12px
 * that nobody asked for. It belongs in the distance moved, never in the decision to move.
 */

/** Breathing room under the input, so it does not sit flush against the keyboard's edge. */
const KEYBOARD_CLEARANCE_PX = 12;

/**
 * The element that will actually move: the nearest ancestor that scrolls, or the window.
 *
 * `overflow: clip` is deliberately NOT a scroller (the dashboard shell relies on that), and
 * `getComputedStyle` reports it as `clip` rather than `auto`/`scroll`, so this walks past it
 * exactly as it should.
 */
const nearestScroller = (from: HTMLElement): HTMLElement | Window => {
  let node: HTMLElement | null = from.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return window;
};

export function useKeepAboveKeyboard(
  /**
   * The SURFACE that holds the fields — the popover's Paper, the inline block. Whatever
   * inside it holds focus is what gets revealed; see the header.
   */
  elementRef: RefObject<HTMLElement | null>,
  /** Only while the surface is on screen. */
  enabled: boolean
): void {
  useEffect(() => {
    if (!enabled) return undefined;

    const viewport = window.visualViewport;
    if (!viewport) return undefined;

    const reveal = (): void => {
      const element = elementRef.current;
      if (!element) return;

      /**
       * ONLY CHASE AN ELEMENT THAT HOLDS FOCUS. This is what stops the hook fighting the
       * user: once they have dismissed the keyboard or tapped elsewhere, a visual-viewport
       * event must not yank the page back to an input they have finished with. While they
       * ARE typing in it, keeping it on screen is the whole point.
       */
      const focused = document.activeElement;
      if (!focused || !(focused instanceof HTMLElement)) return;
      if (!(focused === element || element.contains(focused))) return;

      // `getBoundingClientRect` is in LAYOUT-viewport coordinates, and `offsetTop` is where
      // the visual viewport currently sits inside it — so this is the visible bottom edge
      // expressed in the same space as `rect.bottom`.
      const visibleBottom = viewport.offsetTop + viewport.height;
      // The CLEARANCE is not in this test — see the header. Only "is it covered at all".
      const hidden = focused.getBoundingClientRect().bottom - visibleBottom;
      if (hidden <= 0) return;

      /**
       * Instant, not smooth. A smooth scroll emits a stream of `visualViewport` scroll
       * events on the way, and each one would re-measure a still-positive overflow and
       * queue another correction on top of the one already in flight. One measurement, one
       * move, and the next event measures a settled geometry.
       */
      nearestScroller(focused).scrollBy(0, hidden + KEYBOARD_CLEARANCE_PX);
    };

    // `resize` is the keyboard opening or closing; `scroll` is the visual viewport being
    // panned within the layout viewport, which iOS does on its own while the keyboard is up.
    viewport.addEventListener("resize", reveal);
    viewport.addEventListener("scroll", reveal);
    /**
     * `focusin` covers the case no viewport event can: the keyboard is ALREADY up (the
     * realtor moved from one field to another) so nothing resizes, but the newly focused
     * input may still be sitting under it. Keystrokes are deliberately not listened for —
     * re-scrolling on every character is the "fighting the user" failure, and typing does
     * not move the input anyway.
     */
    document.addEventListener("focusin", reveal);

    // The keyboard may already be open when this surface mounts.
    reveal();

    return () => {
      viewport.removeEventListener("resize", reveal);
      viewport.removeEventListener("scroll", reveal);
      document.removeEventListener("focusin", reveal);
    };
  }, [elementRef, enabled]);
}
